import { Bell } from "lucide-react";
import { useMutation } from "convex/react";
import { Link } from "react-router-dom";
import { Button, Card, EmptyState, PageHeader, Tag } from "../components/ui";
import { convexApi } from "../convexApi";
import type { LexData, NotificationItem } from "../lib/types";

type NotificationPersistence = {
  markRead?: (notificationId: string) => Promise<unknown>;
  markAllRead?: () => Promise<unknown>;
};

export function NotificationsPage({ data, mode = "demo" }: { data: LexData; mode?: "convex" | "demo" }) {
  if (mode === "convex") return <LiveNotificationsPage data={data} />;
  return <NotificationsPageContent data={data} />;
}

function LiveNotificationsPage({ data }: { data: LexData }) {
  const markRead = useMutation(convexApi.notifications.markRead);
  const markAllRead = useMutation(convexApi.notifications.markAllRead);
  const persistence: NotificationPersistence = {
    markRead: (notificationId) => markRead({ notificationId: notificationId as never }),
    markAllRead: () => markAllRead({}),
  };
  return <NotificationsPageContent data={data} persistence={persistence} />;
}

function NotificationsPageContent({ data, persistence }: { data: LexData; persistence?: NotificationPersistence }) {
  const unreadCount = data.notifications.filter((notification) => !notification.readAt).length;
  return (
    <>
      <PageHeader
        title="Notifications"
        subtitle="Views, votes, mentions, deadlines and cooperation confirmations."
        actions={unreadCount > 0 ? <Button type="button" variant="secondary" onClick={() => void persistence?.markAllRead?.()}>Mark all read</Button> : null}
      />
      <div className="grid gap-3">
        {data.notifications.length > 0 ? (
          data.notifications.map((notification) => (
            <Card key={notification.id}>
              <div className="flex gap-3">
                <Bell className="mt-1 h-5 w-5 text-primary" />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2 text-sm text-muted">
                    <span>{notification.type}</span>
                    {!notification.readAt ? <Tag>Unread</Tag> : null}
                  </div>
                  <div className="font-medium">{notification.title}</div>
                  <p className="mt-1 text-sm text-secondary">{notification.body}</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Link to={targetHref(notification)} className="inline-flex min-h-9 items-center rounded-lg border border-line bg-surface px-3 text-sm font-semibold text-ink hover:bg-hover">Open item</Link>
                    {!notification.readAt ? <Button type="button" variant="quiet" onClick={() => void persistence?.markRead?.(notification.id)}>Mark read</Button> : null}
                  </div>
                </div>
              </div>
            </Card>
          ))
        ) : (
          <EmptyState>No notifications right now.</EmptyState>
        )}
      </div>
    </>
  );
}

function targetHref(notification: NotificationItem) {
  if (notification.targetType === "governanceProposal") return `/discussions/proposals/${notification.targetId}`;
  if (notification.targetType === "discussion") return `/discussions?discussion=${notification.targetId}`;
  if (notification.targetType === "cooperationRequest") return `/expertise-exchange?request=${notification.targetId}`;
  if (notification.targetType === "legislationFile") return `/legislative-action?file=${notification.targetId}`;
  if (notification.targetType === "firm") return `/members/${notification.targetId}`;
  return "/my-workspace";
}
